import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Camera, Image as ImageIcon, X, Loader2, AlertTriangle } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { compressPhoto, blobToDataUrl, dataUrlToBlob, kb, MAX_BYTES } from "@/lib/photo";
import { setAppBusy, savePending, readPending, clearPending } from "@/lib/busy";

const ALLOWED = /^image\/(jpeg|jpg|png|webp|heic|heif)$/i;

function Uploading({ stage, progress }) {
  return (
    <div className="py-8 flex flex-col items-center gap-3 text-muted-foreground" data-testid="camera-uploading">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
      <span className="text-sm font-medium text-foreground">{stage === "processing" ? "Preparando sua foto..." : "Enviando sua foto..."}</span>
      <Progress value={stage === "processing" ? 5 : progress} className="w-full h-2" data-testid="camera-progress" />
      <span className="text-xs">{stage === "processing" ? "" : `${progress}%`}</span>
    </div>
  );
}

function ErrorBox({ message, onRetry, onPickAgain }) {
  return (
    <div className="py-4 space-y-3" data-testid="camera-error">
      <div className="flex items-start gap-2 rounded-xl border border-destructive/50 bg-destructive/10 p-3 text-sm">
        <AlertTriangle className="w-4 h-4 mt-0.5 text-destructive shrink-0" /> <span data-testid="camera-error-message">{message}</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {onRetry && <button onClick={onRetry} className="h-12 rounded-xl bg-primary text-primary-foreground font-semibold press" data-testid="camera-retry-button">Tentar de novo</button>}
        <button onClick={onPickAgain} className={`h-12 rounded-xl bg-muted font-semibold press ${onRetry ? "" : "col-span-2"}`} data-testid="camera-pick-again">Escolher outra foto</button>
      </div>
    </div>
  );
}

export default function CameraCapture({ open, onClose, onUploaded, label = "Postar foto", context, resume }) {
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const blobRef = useRef(null);
  const pickerOpen = useRef(false);
  const [stage, setStage] = useState("idle");
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  const releasePicker = () => { if (pickerOpen.current) { pickerOpen.current = false; setAppBusy(false); } };

  useEffect(() => () => releasePicker(), []); // eslint-disable-line

  const upload = async (blob) => {
    setStage("uploading"); setProgress(0); setError(null); setAppBusy(true);
    console.info("[foto] envio iniciado", kb(blob.size));
    try {
      const fd = new FormData();
      fd.append("file", blob, "foto.jpg");
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => e.total && setProgress(Math.min(99, Math.round((e.loaded / e.total) * 100))),
      });
      console.info("[foto] resposta do servidor (upload)", data);
      await onUploaded(data.url);
      console.info("[foto] post criado no rolê");
      setProgress(100);
      clearPending();
      blobRef.current = null;
      setStage("idle");
      onClose?.();
    } catch (err) {
      console.error("[foto] erro no envio", err?.response?.status, err?.response?.data || err);
      setError(formatApiError(err) || "Não foi possível enviar a foto.");
      setStage("error");
    } finally { setAppBusy(false); }
  };

  useEffect(() => {
    if (!open || !resume) return;
    console.info("[foto] página recarregou ao voltar da câmera; retomando", { hasPhoto: !!resume.dataUrl });
    if (resume.dataUrl) {
      dataUrlToBlob(resume.dataUrl).then((b) => { blobRef.current = b; upload(b); });
    } else {
      setError("A página recarregou ao voltar da câmera e a foto se perdeu no celular. Toque em \"Tirar foto\" de novo.");
      setStage("error-nofile");
    }
  }, [open, resume]); // eslint-disable-line

  if (!open) return null;

  const openPicker = (ref, source) => {
    savePending({ ...context, screen: "event-sheet", action: "posting_photo", source });
    pickerOpen.current = true;
    setAppBusy(true);
    window.addEventListener("focus", () => setTimeout(releasePicker, 1500), { once: true });
    ref.current?.click();
  };

  const handle = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    releasePicker();
    if (!file) { console.info("[foto] seleção cancelada"); return; }
    console.info("[foto] foto recebida", file.type || "sem tipo", kb(file.size));
    if (file.type && !ALLOWED.test(file.type)) {
      setError("Formato não aceito. Use uma foto JPG, PNG ou WebP."); setStage("error-nofile"); return;
    }
    setStage("processing"); setError(null); setAppBusy(true);
    try {
      const blob = await compressPhoto(file);
      console.info("[foto] comprimida", `${kb(file.size)} → ${kb(blob.size)}`);
      if (blob.size > MAX_BYTES) throw new Error("A foto ficou maior que 2 MB mesmo depois de comprimir. Tente outra.");
      blobRef.current = blob;
      blobToDataUrl(blob).then((dataUrl) => savePending({ ...context, action: "posting_photo", dataUrl })).catch(() => {});
      setAppBusy(false);
      await upload(blob);
    } catch (err) {
      setAppBusy(false);
      console.error("[foto] erro ao preparar a foto", err);
      setError(err.message || "Não consegui preparar a foto.");
      setStage("error-nofile");
    }
  };

  const close = () => { if (stage === "uploading" || stage === "processing") return; clearPending(); setStage("idle"); setError(null); onClose?.(); };
  const pickAgain = () => { setStage("idle"); setError(null); };

  return (
    <div className="fixed inset-0 z-[2100] bg-black/70 backdrop-blur-sm grid place-items-end sm:place-items-center p-4" onClick={close} data-testid="camera-capture-modal">
      <div className="w-full max-w-sm bg-card border border-white/10 rounded-2xl p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg heading font-bold">{label}</h3>
          <button onClick={close} className="h-9 w-9 rounded-full bg-muted grid place-items-center press" data-testid="camera-close"><X className="w-4 h-4" /></button>
        </div>
        {(stage === "processing" || stage === "uploading") && <Uploading stage={stage} progress={progress} />}
        {stage === "error" && <ErrorBox message={error} onRetry={() => blobRef.current && upload(blobRef.current)} onPickAgain={pickAgain} />}
        {stage === "error-nofile" && <ErrorBox message={error} onPickAgain={pickAgain} />}
        {stage === "idle" && (
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => openPicker(cameraRef, "camera")} className="flex flex-col items-center justify-center gap-2 py-6 rounded-xl bg-primary text-primary-foreground press" data-testid="camera-take-button">
              <Camera className="w-8 h-8" />
              <span className="text-sm font-semibold">Tirar foto</span>
            </button>
            <button onClick={() => openPicker(galleryRef, "gallery")} className="flex flex-col items-center justify-center gap-2 py-6 rounded-xl bg-muted press" data-testid="camera-gallery-button">
              <ImageIcon className="w-8 h-8" />
              <span className="text-sm font-semibold">Galeria</span>
            </button>
          </div>
        )}
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handle} data-testid="camera-input" />
        <input ref={galleryRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handle} data-testid="gallery-input" />
      </div>
    </div>
  );
}

export const pendingPhoto = () => {
  const p = readPending();
  return p && p.action === "posting_photo" && p.event_id ? p : null;
};
